import React, { useEffect, useRef, useState } from "react";

import { animated, useSpring } from "@react-spring/web";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import SvgArrowLeft from "@uui/ArrowLeft";
import CheckIcon from "@uui/Check";
import emailValidator from "email-validator";
import { find, findIndex, isEmpty, map, remove } from "lodash/fp";
import { useParams } from "react-router-dom";

import { Collapse, Divider, FormControlLabel, Typography } from "@mui/material";

import api from "@app/common/access/ShareReport/api";
import AutomaticShareDisclaimer from "@app/common/access/ShareReport/AutomaticShareDisclaimer";
import ShareReportFooter from "@app/common/access/ShareReport/ShareReportFooter";
import { useAuth } from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import { ELLIPSIS_SX } from "@app/common/styles";
import type {
  ApiEntityAccessView,
  EntityAccessLevel,
  ShareEntity,
  User,
} from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { lightOrDark } from "@app/embedded/theme/utils";
import { replaceAt } from "@app/utils/arrays";

import Button from "@rui/Button";
import Checkbox from "@rui/Checkbox";
import IconButton from "@rui/IconButton";
import ModalTitle from "@rui/Modal/ModalTitle";
import Stack, { Column, Row } from "@rui/Stack";
import TextField from "@rui/TextField";
import TooltipIcon from "@rui/TooltipIcon";

import AccessList, { AccessLevelChange } from "./AccessList";
import * as Events from "./events";
import useEditSharing from "./hooks/useEditSharing";
import useReportShare, { DEFAULT_ACCESS } from "./hooks/useReportShare";
import useWorkspaceSearch from "./hooks/useWorkspaceSearch";
import SelfPermissionDowngradeConfirm from "./SelfPermissionDowngradeConfirm";
import SelfRemoveAccessConfirm from "./SelfRemoveAccessConfirm";
import ShareReportAddEntities from "./ShareReportAddEntities";
import {
  AccessListCollapse,
  AccessListWrapper,
  Dialog,
  Container,
  ContentContainer,
  CloseButton,
  Header,
} from "./styles";

interface ShareReportProps {
  title?: string;
  open?: boolean;
  onClose: () => void;
  initEntityEmail?: string | null;
  initAccessLevel?: EntityAccessLevel | null;
  fullScreen?: boolean;
  isActiveSession?: boolean;
  needsEditAccess?: boolean;
}

const AnimatedRow = animated(Row);

const shouldAskForConfirmation = (
  entity: ApiEntityAccessView,
  userId: User["id"]
) => {
  return entity.entityId == userId && entity.accessLevel === "editor";
};

const ShareReport = ({
  title,
  initEntityEmail = null,
  initAccessLevel = null,
  open = false,
  onClose,
  fullScreen = false,
  isActiveSession = false,
  needsEditAccess = false,
}: ShareReportProps) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { inWorkspace, workspaceId } = useWorkspace();
  const notifyMessageRef = useRef<any>();

  const userId = user?.id;

  const {
    accessList,
    modifyAccessMutation,
    addAccessMutation,
    removeAccessMutation,
  } = useReportShare({
    sessionId: sessionId!,
  });

  const [confirmingChange, setConfirmingChange] =
    useState<AccessLevelChange | null>(null);
  const [confirmingRemoveEntityId, setConfirmingRemoveEntityId] = useState<
    string | null
  >(null);
  const [notifyViaEmail, setNotifyViaEmail] = useState(true);

  const {
    bulkAccess,
    clearSelectedEntities,
    isAddingEntities,
    onAddEntity,
    onChangeBulkAccess,
    onChangeSelectedEntities,
    selectedEntities,
  } = useEditSharing({
    initialBulkAccessLevel: initAccessLevel,
  });

  const { searchResults } = useWorkspaceSearch(initEntityEmail, workspaceId, {
    enabled: initEntityEmail !== null,
  });

  const requestEditAccessMutation = useMutation({
    mutationFn: api.access.requestAccess,
    onError: (error: any) => {
      Sentry.addBreadcrumb({
        message: "Request access failed",
        level: "error",
        data: { sessionId, accessLevel: "editor" },
      });
      Sentry.captureException(error);
    },
  });

  // This effect handles managing an initially-selected email address
  // to share with and updating the share entity after receiving more
  // data from the workspace search result
  useEffect(() => {
    if (
      !open ||
      initEntityEmail === null ||
      (selectedEntities.includes(initEntityEmail) && isEmpty(searchResults))
    ) {
      return;
    }

    onChangeSelectedEntities((prevState) => {
      const existingIndex = findIndex(
        (email) => email === initEntityEmail,
        map(
          (entity) => (typeof entity === "string" ? entity : entity.email),
          prevState
        )
      );

      const newEntity = searchResults[0] ?? initEntityEmail;

      if (existingIndex !== -1) {
        return replaceAt(prevState, newEntity, existingIndex);
      }

      return [...prevState, newEntity];
    });
  }, [searchResults, initEntityEmail, open]);

  useEffect(() => {
    if (open) return;
    clearSelectedEntities();
  }, [open]);

  const handleRequestEditAccess = () => {
    requestEditAccessMutation.mutate(
      {
        reportId: sessionId!,
        reportType: "meeting",
        accessLevel: "editor",
        message: "",
      },
      {
        onSuccess: () => {
          showToast({
            title: "Editor access requested",
            message:
              "When the report owner approves your request, you will receive an email confirmation.",
            type: "system",
          });
        },
      }
    );
  };

  const handleChangeSelectedEntities = (newValue: ShareEntity[]) => {
    if (newValue.length > selectedEntities.length) {
      Events.shareReportAddPeople({ count: newValue.length });
    }
    onChangeSelectedEntities(newValue);
  };

  const handleRemoveAccess = (entityId: string, skipConfirmation = false) => {
    const entity = find({ entityId }, accessList.entityAccess);
    if (!entity || !sessionId) return;

    if (!skipConfirmation && entityId == userId) {
      setConfirmingRemoveEntityId(entityId);
      return;
    }

    removeAccessMutation.mutate(
      { sessionId, entity },
      {
        onSuccess: () => {
          if (entityId == userId) {
            // if the user just removed their own access, reload the page to fetch the
            // report again w/ the updated permissions (could still have some level of access
            // via workspace admin/team or link access)
            window.location.reload();
          }
        },
        onError: () => {
          showToast({
            message: "There was a problem. Try again or contact support.",
            severity: "error",
          });
        },
      }
    );
  };

  const handleShare = () => {
    // filter out bad email input
    const candidates = remove<ShareEntity>(
      (entity) =>
        typeof entity === "string" && !emailValidator.validate(entity),
      selectedEntities
    );

    if (candidates.length !== selectedEntities.length) {
      showToast({
        message: "Please enter valid email addresses",
        severity: "error",
      });
      return;
    }

    const notifyMessage: string | undefined = notifyViaEmail
      ? notifyMessageRef.current?.value
      : undefined;

    const entities = candidates.map((c) => ({
      entityId: typeof c === "string" ? c : c.id,
      kind: typeof c === "string" ? "email" : c.type,
      name: typeof c === "string" ? c : c.name,
      accessLevel: bulkAccess,
      notifyUser: notifyViaEmail,
      message: notifyMessage,
    }));

    if (sessionId) {
      Events.shareReportSuccess({ type: "meeting" });
      addAccessMutation.mutate(
        {
          sessionId,
          entities,
        },
        {
          onError: () => {
            showToast({
              message: "There was a problem. Try again or contact support.",
              severity: "error",
            });
          },
          onSuccess: () => {
            showToast({ message: "Shared successfully!", severity: "success" });
            clearSelectedEntities();
          },
        }
      );
    }
  };

  // called when a user modifies the access level of an entity
  const handleAccessListChange = ({
    entityId,
    accessLevel,
    skipConfirmation = false,
  }: AccessLevelChange) => {
    const targetEntity = find({ entityId }, accessList.entityAccess);

    if (!targetEntity || !sessionId) return;

    if (
      !skipConfirmation &&
      userId &&
      shouldAskForConfirmation(targetEntity, userId)
    ) {
      setConfirmingChange({ entityId, accessLevel, skipConfirmation: true });
      return;
    }

    modifyAccessMutation.mutate(
      {
        entity: { ...targetEntity, accessLevel },
        sessionId,
      },
      {
        onSuccess: () => {
          if (targetEntity.entityId == userId) {
            // if the user just changed their own permissions, reload the page to fetch the
            // report again w/ the updated permissions
            window.location.reload();
          }
        },
        onError: () => {
          showToast({
            message: "There was a problem. Try again or contact support.",
            severity: "error",
          });
        },
      }
    );
  };

  const handleConfirmChangePermissions = () => {
    // shouldn't ever hit this, but include to appease types
    if (!confirmingChange) return;

    handleAccessListChange(confirmingChange);
  };

  const handleConfirmRemoveSelfAccess = () => {
    // shouldn't ever hit this, but include to appease types
    if (!confirmingRemoveEntityId) return;
    handleRemoveAccess(confirmingRemoveEntityId, true);
  };

  const backArrowProps = useSpring({
    opacity: isAddingEntities ? 1 : 0,
    width: isAddingEntities ? "44px" : "0px",
    delay: isAddingEntities ? 0 : 400,
    marginRight: isAddingEntities ? 8 : 0,
    config: {
      tension: 500,
    },
  });

  const modalTitle = `Share Report${title ? `: ${title}` : ""}`;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen={fullScreen}>
        <Container spacing={1.5}>
          <Header>
            <Row
              variant="apart"
              sx={{ overflow: fullScreen ? "hidden" : undefined }}
            >
              <AnimatedRow style={backArrowProps} variant="left">
                <IconButton onClick={clearSelectedEntities}>
                  <SvgArrowLeft sx={{ width: "20px", height: "20px" }} />
                </IconButton>
              </AnimatedRow>
              <ModalTitle
                title={modalTitle}
                sx={{
                  ...(fullScreen ? ELLIPSIS_SX : {}),
                  color: (theme) =>
                    lightOrDark(
                      "shades.neutral.700",
                      "shades.neutral.50"
                    )(theme),
                }}
              >
                {modalTitle}
              </ModalTitle>
            </Row>
            <CloseButton onClick={onClose} />
          </Header>
          <ContentContainer spacing={1.5}>
            {!isAddingEntities && <AutomaticShareDisclaimer user={user} />}
            {needsEditAccess ? (
              <Column spacing={1} variant="left">
                <Typography variant="bodyPrimaryReg" color="shades.neutral.400">
                  You're a viewer and can't manage sharing access
                </Typography>
                <Button
                  variant={
                    requestEditAccessMutation.isSuccess ? "text" : "outlined"
                  }
                  startIcon={
                    requestEditAccessMutation.isSuccess ? (
                      <CheckIcon />
                    ) : undefined
                  }
                  sx={{
                    color: requestEditAccessMutation.isSuccess
                      ? "shades.meadow.600"
                      : undefined,
                  }}
                  loading={requestEditAccessMutation.isPending}
                  onClick={handleRequestEditAccess}
                >
                  {requestEditAccessMutation.isSuccess
                    ? "Editor access requested"
                    : "Request editor access"}
                </Button>
                <Divider sx={{ width: "100%", mt: "8px" }} />
              </Column>
            ) : (
              <>
                <ShareReportAddEntities
                  accessList={accessList}
                  bulkAccess={bulkAccess}
                  fullScreen={fullScreen}
                  isAddingEntities={isAddingEntities}
                  onChangeBulkAccess={onChangeBulkAccess}
                  onInputChange={handleChangeSelectedEntities}
                  onSelectSuggestion={onAddEntity}
                  selectedEntities={selectedEntities}
                />
                <Collapse in={isAddingEntities} unmountOnExit>
                  <Stack spacing={1}>
                    {fullScreen && <Divider />}
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ mr: 0.5 }}
                          onChange={(e) => setNotifyViaEmail(e.target.checked)}
                        />
                      }
                      checked={notifyViaEmail}
                      label={
                        <Typography
                          variant="bodySecondaryReg"
                          sx={{
                            color: (theme) =>
                              lightOrDark(
                                "shades.neutral.700",
                                "shades.neutral.200"
                              )(theme),
                          }}
                        >
                          Notify via email
                        </Typography>
                      }
                      sx={{ mx: 0.5 }}
                    />
                    <Collapse in={notifyViaEmail} unmountOnExit>
                      <TextField
                        placeholder="Add a message (optional)"
                        multiline
                        sx={{ width: "100%", p: 0, minWidth: 0 }}
                        ref={notifyMessageRef}
                        InputProps={{ sx: { p: 0 } }}
                      />
                    </Collapse>
                  </Stack>
                </Collapse>
                <AccessListCollapse in={!isAddingEntities}>
                  <AccessListWrapper spacing={2}>
                    <Column spacing={1} flexGrow={{ xxs: 1, sm: undefined }}>
                      <Row variant="left" spacing={1}>
                        <Typography
                          variant="bodyPrimaryMed"
                          sx={{
                            color: (theme) =>
                              lightOrDark(
                                "shades.neutral.800",
                                "shades.neutral.50"
                              )(theme),
                          }}
                        >
                          People with access
                        </Typography>
                        {inWorkspace && (
                          <TooltipIcon title="By default, your Workspace Admins also have report access" />
                        )}
                      </Row>
                      <AccessList
                        entities={accessList.entityAccess}
                        onChange={handleAccessListChange}
                        onRemove={handleRemoveAccess}
                        useAccessLevelIcons={fullScreen}
                      />
                    </Column>
                  </AccessListWrapper>
                </AccessListCollapse>
              </>
            )}
          </ContentContainer>
          <ShareReportFooter
            fullScreen={fullScreen}
            generalAccess={
              accessList.generalAccess ?? DEFAULT_ACCESS.generalAccess!
            }
            isAddingUsers={isAddingEntities}
            isLoading={addAccessMutation.isPending}
            onClose={onClose}
            onShare={handleShare}
            sessionId={sessionId!}
            isActiveSession={isActiveSession}
            needsEditAccess={needsEditAccess}
          />
        </Container>
      </Dialog>
      <SelfPermissionDowngradeConfirm
        open={Boolean(confirmingChange)}
        onClose={() => setConfirmingChange(null)}
        onConfirm={handleConfirmChangePermissions}
        loading={modifyAccessMutation.isPending}
      />
      <SelfRemoveAccessConfirm
        open={Boolean(confirmingRemoveEntityId)}
        onClose={() => setConfirmingRemoveEntityId(null)}
        onConfirm={handleConfirmRemoveSelfAccess}
        loading={removeAccessMutation.isPending}
      />
    </>
  );
};

export default ShareReport;
